import React, { Fragment, PureComponent, useState, useMemo } from 'react';
import { I } from '../../../common/v5/config';
import { AreaDropdown, ActiveBorderHighlightArraySingleSelect as SingleSelect } from '../../../reactcomponents/Dropdown';
import {
	INPUT_EMPTY_WARNING,
	ICON_CUSTOM_CHANNEL,
	CHANNEL_ICONS_MAP as iconMap
} from '../../../common/v5/constants';
import { renderFileActionBtns as renderActionButtons } from '../admin';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import ToolkitProvider, { Search } from 'react-bootstrap-table2-toolkit';
import { customPageTotal, TABLE_STANDARD_OPTIONS } from '../../../reactcomponents/Table';
import {
	TextInputRow,
	SelectInputRow,
	CustomInputRow,
	TableIconicButton as Button
} from '../../../reactcomponents/Form';
import { TXT_NO_SELECTION } from '../../../common/v5/receiptGreetingConstants';
import { addressValidation } from '../../../common/v5/helpers';

const getChannelIcon = (channel) => {
	let iconClass = iconMap[channel] || ICON_CUSTOM_CHANNEL
	return iconClass;
}

const warnTxt = INPUT_EMPTY_WARNING;

// Component for adding new channel-address pairs
const AddressBookEntryForm = ({
	input,
	channels,
	onAddEntry,
	onCancel,
	modal = false
}) => {
	const [touched, setTouched] = useState(false);
	const [selectedChannel, setSelectedChannel] = useState(input.selectedChannel || 1);
	const [address, setAddress] = useState("");

	const getPlaceholderByChannelId = (channelId) => {
		return "Enter Address";
	};

	const handleSave = () => {
		if (selectedChannel && address.trim()) {
			const trimmedAddress = address.trim();

			// Check if address contains commas (multiple addresses)
			const addresses = trimmedAddress.includes(',')
				? trimmedAddress.split(',').map(addr => addr.trim()).filter(addr => addr.length > 0)
				: [trimmedAddress];

			// Validate each address individually
			const validAddresses = [];
			const invalidAddresses = [];

			for (const addr of addresses) {
				// For single address validation, don't use supportMultiAddress
				if (addressValidation(selectedChannel, addr, false)) {
					validAddresses.push(addr);
				} else {
					invalidAddresses.push(addr);
				}
			}
			
			// If there are invalid addresses, show error and don't proceed
			if (invalidAddresses.length > 0) {
				alert(I("* Please insert correct contact address"));
				return;
			}
			
			// Create entries for each valid address
			const channelName = channels.find(c => c.id === selectedChannel)?.name || `Channel ${selectedChannel}`;

			const newEntries = validAddresses.map((addr, index) => ({
				id: Date.now() + index, // temporary ID for new entries, with offset to ensure uniqueness
				serviceType: selectedChannel,
				address: addr,
				service: channelName
			}));

			// Pass array of entries to onAddEntry
			onAddEntry(newEntries);

			setAddress("");
			setSelectedChannel(input.selectedChannel || 1);
			setTouched(false);
		}
	};

	const isValid = selectedChannel && address.trim();

	return (
		<div className="form-container clearfix">
			<div className="input-wrapper">
				<CustomInputRow
					id={modal ? "channelPopup" : "selectedChannel"}
					name="selectedChannel"
					className={"admin-select-channel"}
				>
					<SingleSelect
						data={channels.filter(channel => channel.id !== undefined && channel.id !== null)}
						invalidSelectedText={TXT_NO_SELECTION}
						onSelect={(v) => setSelectedChannel(v)}
						selected={selectedChannel}
						idFields={{ id: "id", name: "name" }}
					/>
				</CustomInputRow>
				<TextInputRow
					id={modal ? "addressBookEntryPopup" : "addressBookEntry"}
					name="addressBookEntry"
					className="admin-text-input addressbook"
					value={address}
					placeholder={getPlaceholderByChannelId(selectedChannel)}
					onChange={(e) => setAddress(e.target.value)}
					onBlur={() => setTouched(true)}
					mandatory={true}
					warning={(!address && touched) && warnTxt}
					helperTxt={I("To insert multiple addresses, use comma to separate each email")}
				/>
			</div>
			<div className='action-wrapper' style={{display: 'flex', gap: '10px'}}>
				<Button type="button" onClick={handleSave} disabled={!isValid} className="btn-blue" title="Add Entry" data-qa-id="btn-save-entry" label={I("Add Entry")}/>
				<Button type="button" onClick={onCancel} className="btn-blue" title="Cancel" data-qa-id="btn-cancel-entry" label={I("Cancel")}/>
			</div>
		</div>
	);
};

// Component for displaying the list of channel-address pairs
const AddressBookEntryList = ({
	list,
	onDeleteEntry,
	editable = true,
	itemsPerPage = 5
}) => {
	const handleDeleteEntry = (entryId) => {
		let confirmDelMsg = I('Are you sure you want to delete?');

		if (confirm(confirmDelMsg)) { // TODO: avoid using primitive confirm box
			onDeleteEntry(entryId);
		}
	}

	const serviceFormatter = (cell, row) => {
		let service = row.service
		return (
			<div className='custom-col service'>
				<i className={getChannelIcon(service)}></i>
				<span className='label' style={{marginLeft:"10px"}}>{cell}</span>
			</div>
		)
	}

	const actionDatas = {
		onDelete: handleDeleteEntry,
	}

	const columns = [
		{
			dataField: 'id',
			text: 'ID',
			sort: true,
			hidden: true
		},
		{
			dataField: 'service',
			text: 'Channel',
			sort: true,
			formatter: serviceFormatter
		},
		{
			dataField: 'address',
			text: 'Address',
			sort: true,
		},
		{
			isDummyField: true,
			dataField: 'action',
			text: 'Action',
			headerClasses: 'center',
			formatter: (cell, row) => {
				return renderActionButtons(cell, row, actionDatas);
			},
			hidden: !editable
		}
	];

	const defaultSorted = [{
		dataField: 'id',
		order: 'desc'
	}];

	const options = {
		paginationSize: 3,
		pageStartIndex: 1,
		hideSizePerPage: true, // Hide the sizePerPage dropdown always
		hidePageListOnlyOnePage: true, // Hide the pagination list when only one page
		firstPageText: '<<',
		prePageText: '<',
		nextPageText: '>',
		lastPageText: '>>',
		showTotal: true,
		paginationTotalRenderer: customPageTotal,
		disablePageTitle: true,
		sizePerPageList: [itemsPerPage]
	};

	return (
		<div className="table-wrapper v5-table-style">
			<ToolkitProvider
				keyField="id"
				data={list}
				columns={columns}
				search
			>
				{
					props => (
						<BootstrapTable
							keyField="id"
							data={list}
							noDataIndication={I("No data to display")}
							columns={columns}
							bordered={false}
							condensed={false}
							defaultSorted={defaultSorted}
							pagination={paginationFactory(options)}
							hover
							{...props.baseProps}
						/>
					)
				}
			</ToolkitProvider>
		</div>
	)
};

// Container component for managing address book entries
const AddressBookEntriesContainer = ({
	input,
	channels,
	onAddEntry,
	onDeleteEntry,
	modal = false,
	editable = true
}) => {
	const [showForm, setShowForm] = useState(false);

	// Convert single address to array format for backward compatibility
	// Use useMemo to ensure entries are recalculated when input changes
	// Prioritize addressBookEntries over addressBookEntry - if addressBookEntries has value, ignore addressBookEntry completely
	const entries = useMemo(() => {
		// If addressBookEntries exists and has content, use it exclusively
		if (input.addressBookEntries && input.addressBookEntries.length > 0) {
			return input.addressBookEntries;
		}
		
		// Fallback to single addressBookEntry for backward compatibility
		return input.addressBookEntry ? [{
			id: 1,
			serviceType: input.selectedChannel || 1,
			address: input.addressBookEntry,
			service: channels.find(c => c.id === (input.selectedChannel || 1))?.name || `Channel ${input.selectedChannel || 1}`
		}] : [];
	}, [input.addressBookEntries, input.addressBookEntry, input.selectedChannel, channels]);

	const handleAddEntry = (newEntryOrEntries) => {
		onAddEntry(newEntryOrEntries);
		setShowForm(false);
	};

	const handleCancelForm = () => {
		setShowForm(false);
	};

	return (
		<div className='addressbook-entries-section'>
			{showForm ? (
				<AddressBookEntryForm
					input={input}
					channels={channels}
					onAddEntry={handleAddEntry}
					onCancel={handleCancelForm}
					modal={modal}
				/>
			) : (
				<>
					<div style={{marginTop:"20px", marginBottom:"20px"}}>
						<Button type="button"
							className="btn-blue add btn-round"
							onClick={() => setShowForm(true)}
							title={I("Add Entry")}
							data-qa-id="btn-toggle-entry-form"
							iconClass='icon-add'
							label={I("Add Entry")}
						/>
					</div>
				</>
			)}
			{entries.length > 0 && (
				<AddressBookEntryList
					list={entries}
					onDeleteEntry={onDeleteEntry}
					editable={editable}
				/>
			)}
		</div>
	);
};

export { AddressBookEntryForm, AddressBookEntryList, AddressBookEntriesContainer };
